// Рисование разобранной статьи справки (блоки — lib/help-markdown.ts). HTML строкой не
// вставляем нигде: каждый блок рисуется своим тегом, поэтому чужой разметке в текст
// справки не попасть. Вид — стили .legal (общий каркас документа) + help.css.
import type { HelpBlock, HelpInline } from '@/lib/help-markdown';
import { useTranslations } from 'next-intl';
import { HelpScene } from './help-scene';
import { HELP_FIRST_IMAGES } from '@/lib/app-pages/warm-plan';

function Inline({ parts }: { parts: HelpInline[] }) {
  return (
    <>
      {parts.map((p, i) => {
        if (p.k === 'b') return <strong key={i}>{p.s}</strong>;
        if (p.k === 'i') return <em key={i}>{p.s}</em>;
        if (p.k === 'code') return <code key={i}>{p.s}</code>;
        // QUICK-START §11.22: розовый кружок с плюсом — та же кнопка шапки «Ботов», в строке текста.
        if (p.k === 'text' && 'icon' in p && p.icon === 'plus') return <span key={i} className="help-ico-plus" role="img" aria-label={p.s} />;
        if (p.k === 'a')
          return (
            <a key={i} href={p.href} {...(/^https?:/.test(p.href) ? { rel: 'noopener' } : {})}>
              {p.s}
            </a>
          );
        return <span key={i}>{p.s}</span>;
      })}
    </>
  );
}

/** Содержание инструкции: серая подсказка над ним (без заголовка — владелец 26.10.2026), пункты-ссылки на свои разделы (владелец 26.10.2026). */
function HelpPlan({ items }: { items: Extract<HelpBlock, { k: 'toc' }>['items'] }) {
  const t = useTranslations('help');
  return (
    <nav className="help-plan-box" aria-label={t('toc')}>
      <p className="help-plan-hint">{t('tocHint')}</p>
      <ol className="help-plan">
        {items.map((it, j) => {
          const row = (
            <>
              <span className="help-plan-num">{j + 1}</span>
              <span className="help-plan-title">{it.text}</span>
              {it.note ? <span className="help-plan-note">{it.note}</span> : null}
            </>
          );
          return (
            <li key={j}>
              {it.id ? <a href={`#${it.id}`}>{row}</a> : <span className="help-plan-row">{row}</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function HelpMarkdown({ blocks }: { blocks: HelpBlock[] }) {
  // Первые HELP_FIRST_IMAGES снимков статьи — первый экран: высокая важность; остальные — ленивые (SPEED §6.3).
  const firstImgs = new Set(
    blocks
      .map((b, i) => (b.k === 'img' ? i : -1))
      .filter((i) => i >= 0)
      .slice(0, HELP_FIRST_IMAGES),
  );
  return (
    <>
      {blocks.map((b, i) => {
        switch (b.k) {
          case 'h': {
            const inner = (
              // Опора для «Содержания»: сама ссылка ведёт на этот же заголовок, чтобы
              // человек мог скопировать адрес нужного места статьи.
              <a className="help-anchor" href={`#${b.id}`} id={b.id}>
                {b.text}
              </a>
            );
            if (b.level === 2) return <h2 key={i}>{inner}</h2>;
            if (b.level === 3) return <h3 key={i}>{inner}</h3>;
            return <h4 key={i}>{inner}</h4>;
          }
          case 'p':
            return (
              <p key={i}>
                <Inline parts={b.inline} />
              </p>
            );
          case 'ul':
            return (
              <ul key={i}>
                {b.items.map((it, j) => (
                  <li key={j}>
                    <Inline parts={it} />
                  </li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={i} {...(b.start ? { start: b.start } : {})}>
                {b.items.map((it, j) => (
                  <li key={j}>
                    <Inline parts={it} />
                  </li>
                ))}
              </ol>
            );
          case 'toc':
            return <HelpPlan key={i} items={b.items} />;
          case 'quote':
            return (
              <blockquote key={i}>
                <Inline parts={b.inline} />
              </blockquote>
            );
          case 'table':
            return (
              // Широкая таблица уезжает вбок ВНУТРИ своей рамки: горизонтального
              // прокруга у самой страницы быть не должно (канон каркаса).
              <div className="help-tw" key={i}>
                <table>
                  <thead>
                    <tr>
                      {b.head.map((c, j) => (
                        <th key={j}>
                          <Inline parts={c} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, j) => (
                      <tr key={j}>
                        {row.map((c, k) => (
                          <td key={k}>
                            <Inline parts={c} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'pre':
            return (
              <pre key={i}>
                <code>{b.s}</code>
              </pre>
            );
          case 'img':
            // Снимок шага инструкции: размер — из файла (help-content.ts), до загрузки место уже занято.
            return (
              <figure className="help-img" key={i}>
                {/* eslint-disable-next-line @next/next/no-img-element -- статичный снимок из public с готовыми размерами; оптимизатор картинок справке не нужен */}
                <img
                  src={b.src}
                  alt={b.alt}
                  loading="lazy"
                  decoding="async"
                  {...(firstImgs.has(i) ? { fetchPriority: 'high' as const } : {})}
                  {...(b.width && b.height ? { width: b.width, height: b.height } : {})}
                />
              </figure>
            );
          case 'goal':
            // §8.16: `fixed` — сцена закреплена за примером статьи (её слова, без ниши и лица читателя).
            return <HelpScene key={i} scene={b.scene} {...('fixed' in b ? { fixed: b.fixed, ...(b.person ? { person: b.person } : {}) } : {})} />;
          case 'scene':
            // Живой пример (`::scene s6`): нарисованный телефон со сценой, у вошедшего —
            // со словами его ниши и его лицом (help-scene.tsx).
            return <HelpScene key={i} scene={b.scene} />;
          case 'hr':
          default:
            return <hr key={i} />;
        }
      })}
    </>
  );
}
